import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/InvoiceLayout";
import config from "../config.js";
import { useSelector } from "react-redux";

const InventoryManagement = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    supplier: "",
    purchaseDate: "",
    quantityInStock: "",
    reorderLevel: "",
    expiryDate: "",
    warehouseLocation: "",
    costPerUnit: "",
    totalCost: "",
    gstRate: "",
    discount: "",
  });

  useEffect(() => {
    if (selectedCompanyId) {
      fetchInventoryItems();
    }
  }, [selectedCompanyId]);

  const fetchInventoryItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getInventory/${selectedCompanyId}`
      );
      
      // Ensure we're always storing an array in state
      if (response.data && Array.isArray(response.data)) {
        setInventoryItems(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Handle case where the array might be nested in a data property
        setInventoryItems(response.data.data);
      } else {
        // If neither is an array, set to empty array and log error
        console.error("Invalid response format:", response.data);
        setInventoryItems([]);
        setError("Received invalid data format from server");
      }
    } catch (err) {
      console.error("Failed to fetch inventory items:", err);
      setError("Failed to load inventory items. Please try again later.");
      setInventoryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    try {
      if (selectedItem) {
        await axios.put(
          `${API_BASE_URL}/api/v1/updateInventory/${selectedItem._id}`,
          formData
        );
        showSnackbar("Item updated successfully", "success");
      } else {
        await axios.post(
          `${API_BASE_URL}/api/v1/inventory/${selectedCompanyId}`,
          formData
        );
        showSnackbar("Item added successfully", "success");
      }
      fetchInventoryItems();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save inventory item:", err);
      showSnackbar("Failed to save inventory item. Please try again.", "error");
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setFormData(item);
      setSelectedItem(item);
    } else {
      setFormData({
        name: "",
        category: "",
        supplier: "",
        purchaseDate: "",
        quantityInStock: "",
        reorderLevel: "",
        expiryDate: "",
        warehouseLocation: "",
        costPerUnit: "",
        totalCost: "",
        gstRate: "",
        discount: "",
      });
      setSelectedItem(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/deleteInventory/${itemId}`);
      fetchInventoryItems();
      showSnackbar("Item deleted successfully", "success");
    } catch (err) {
      console.error("Failed to delete inventory item:", err);
      showSnackbar(
        "Failed to delete inventory item. Please try again.",
        "error"
      );
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Layout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Paper style={{ padding: 16, margin: 16 }}>
        <Typography variant="h5" gutterBottom>
          Inventory Management
        </Typography>
        {!selectedCompanyId ? (
          <Typography variant="body1">Please select a company</Typography>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            style={{ marginBottom: 16, color: "white", backgroundColor: "red" }}
          >
            Add New Item
          </Button>
        )}
       
        {error && (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
       
        {!error && inventoryItems.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="200px"
          >
            <Typography variant="h6" gutterBottom>
              Your inventory is empty
            </Typography>
            <Typography variant="body1" gutterBottom>
              Click 'Add New Item' to start managing your inventory
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Reorder Level</TableCell>
                  <TableCell>Cost Per Unit</TableCell>
                  <TableCell>Total Cost</TableCell>
                  <TableCell>GST Rate</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(inventoryItems) && inventoryItems.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{item.supplier}</TableCell>
                    <TableCell>{item.quantityInStock}</TableCell>
                    <TableCell>{item.reorderLevel}</TableCell>
                    <TableCell>${item.costPerUnit?.toFixed(2)}</TableCell>
                    <TableCell>${item.totalCost?.toFixed(2)}</TableCell>
                    <TableCell>{item.gstRate}%</TableCell>
                    <TableCell>{item.warehouseLocation}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpenDialog(item)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteItem(item._id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>
            {selectedItem ? "Edit Inventory Item" : "Add New Inventory Item"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Purchase Date"
                  name="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantity in Stock"
                  name="quantityInStock"
                  type="number"
                  value={formData.quantityInStock}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Reorder Level"
                  name="reorderLevel"
                  type="number"
                  value={formData.reorderLevel}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  name="expiryDate"
                  type="date"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Warehouse Location"
                  name="warehouseLocation"
                  value={formData.warehouseLocation}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cost Per Unit"
                  name="costPerUnit"
                  type="number"
                  value={formData.costPerUnit}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Total Cost"
                  name="totalCost"
                  type="number"
                  value={formData.totalCost}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="GST Rate"
                  name="gstRate"
                  type="number"
                  value={formData.gstRate}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Discount"
                  name="discount"
                  type="number"
                  value={formData.discount}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleClick} color="primary">
              {selectedItem ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Layout>
  );
};

export default InventoryManagement;
